import { useState } from 'react';
import { Callout, Checkbox, Checklist, Drawer, Field, Input, Select, Table, Tabs, Textarea } from '../primitives';
import type { ChecklistItem } from '../primitives';
import type { ChannelPolicy, ChannelSettings, DistributionChannel, VariantPublication } from '../../api';
import {
  checkDistributionChannelHealth,
  deleteDistributionCategoryMapping,
  deleteDistributionChannel,
  listDistributionCategoryMappings,
  rotateDistributionFeedToken,
  setDistributionCategoryMapping,
  setDistributionChannelCredential,
  updateDistributionChannel,
} from '../../api';
import { useResource } from '../../hooks';
import { LevelBadge } from '../project/ui';
import { ConfirmDialog, ErrorNotice, LoadingRows } from '../ui';
import {
  channelDescription,
  channelHealthLabel,
  channelHealthLevel,
  channelLabel,
  variantPublicationLabel,
} from './labels';

type Tab = 'connection' | 'policy' | 'categories';

/**
 * A channel's own settings: what it needs to reach the provider, which of
 * the shop's products it may offer, and how its categories map.
 *
 * Credentials go in and never come back out: the field shows whether one is
 * configured, never what it is.
 */
export function ChannelSettingsDrawer({
  channel,
  canManage,
  onClose,
  onChanged,
}: {
  channel: DistributionChannel;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<Tab>('connection');
  const [settings, setSettings] = useState<ChannelSettings>(channel.settings ?? {});
  const [policy, setPolicy] = useState<ChannelPolicy>(channel.policy);
  const [credential, setCredential] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const requiredLeft = (channel.setup ?? []).filter((item) => item.required).length;

  const act = async (key: string, action: () => Promise<unknown>, message?: string) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await action();
      if (message) setNotice(message);
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Drawer
        open
        size="wide"
        title={channelLabel[channel.channel_type]}
        description={
          <span className="drawer-meta">
            {channel.scope}
            <LevelBadge level={channelHealthLevel[channel.enabled ? channel.health : 'disabled']}>
              {channelHealthLabel[channel.enabled ? channel.health : 'disabled']}
            </LevelBadge>
          </span>
        }
        onClose={onClose}
        busy={confirmDelete}
        testId="channel-settings"
        footer={
          <>
            {canManage ? (
              <button type="button" className="button button--danger-quiet dialog__footer-start" onClick={() => setConfirmDelete(true)}>
                Remove this channel
              </button>
            ) : null}
            <button type="button" className="button button--secondary" onClick={onClose}>
              Close
            </button>
          </>
        }
      >
        <Tabs
          label="Channel settings sections"
          variant="secondary"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'connection', label: 'Connection' },
            { value: 'policy', label: 'What is published' },
            { value: 'categories', label: 'Categories' },
          ]}
        />

          {error ? <Callout tone="danger">{error}</Callout> : null}
          {notice ? <Callout tone="success">{notice}</Callout> : null}

          {tab === 'connection' ? (
            <>
              <p className="drawer-intro">{channelDescription[channel.channel_type]}</p>

              <SetupChecklist
                channel={channel}
                busy={busy !== null}
                canManage={canManage}
                onCheck={() => act('health', () => checkDistributionChannelHealth(channel.id))}
              />

              {channel.channel_type !== 'compari' ? (
                <Field
                  label={credentialLabel[channel.channel_type]}
                  hint="Stored encrypted; never shown again, never in a payload preview or a log."
                  action={
                    <button
                      type="button"
                      className="button button--secondary"
                      disabled={!canManage || credential.trim() === '' || busy !== null}
                      onClick={() =>
                        act(
                          'credential',
                          async () => {
                            await setDistributionChannelCredential(channel.id, credential);
                            setCredential('');
                          },
                          'Credential saved.',
                        )
                      }
                    >
                      {busy === 'credential' ? 'Saving' : 'Save credential'}
                    </button>
                  }
                >
                  <Textarea
                    id="channel-credential"
                    rows={channel.channel_type === 'meta_commerce' ? 2 : 4}
                    value={credential}
                    disabled={!canManage}
                    placeholder={
                      channel.credential_configured
                        ? 'A credential is stored. Paste a new one to replace it.'
                        : 'Paste the credential here.'
                    }
                    onChange={(event) => setCredential(event.target.value)}
                  />
                </Field>
              ) : null}

              {channel.channel_type === 'google_merchant' ? (
                <GoogleFields settings={settings} onChange={setSettings} disabled={!canManage} />
              ) : null}
              {channel.channel_type === 'openai_acp' ? (
                <OpenAIFields settings={settings} onChange={setSettings} disabled={!canManage} />
              ) : null}
              {channel.channel_type === 'meta_commerce' ? (
                <MetaFields
                  channel={channel}
                  settings={settings}
                  onChange={setSettings}
                  disabled={!canManage}
                  busy={busy}
                  onRotate={() =>
                    act('feed-token', () => rotateDistributionFeedToken(channel.id), 'A new feed URL has been issued. The old one stops working, so update the data source in Commerce Manager.')
                  }
                />
              ) : null}
              {channel.channel_type === 'compari' ? (
                <CompariFields
                  channel={channel}
                  settings={settings}
                  onChange={setSettings}
                  disabled={!canManage}
                  busy={busy}
                  onRotate={() =>
                    act('feed-token', () => rotateDistributionFeedToken(channel.id), 'A new feed URL has been issued. The old one stops working.')
                  }
                />
              ) : null}

              {canManage ? (
                <button
                  type="button"
                  className="button button--primary"
                  disabled={busy !== null}
                  onClick={() => act('settings', () => updateDistributionChannel(channel.id, { settings }), 'Settings saved.')}
                  data-testid="channel-save-settings"
                >
                  {busy === 'settings' ? 'Saving' : 'Save settings'}
                </button>
              ) : null}

              <Checkbox
                checked={channel.enabled}
                disabled={!canManage || busy !== null || (!channel.enabled && requiredLeft > 0)}
                onChange={(event) =>
                    act('enabled', () => updateDistributionChannel(channel.id, { enabled: event.target.checked }))
                  }
                label={<>Publish this shop’s products to {channelLabel[channel.channel_type]}</>}
              />

            </>
          ) : null}

          {tab === 'policy' ? (
            <PolicyFields
              policy={policy}
              disabled={!canManage}
              onChange={setPolicy}
              onSave={() =>
                act(
                  'policy',
                  () => updateDistributionChannel(channel.id, { policy }),
                  'Policy saved.',
                )
              }
              busy={busy === 'policy'}
            />
          ) : null}

          {tab === 'categories' ? (
            <CategoryMappings connectionId={channel.id} channelType={channel.channel_type} canManage={canManage} />
          ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirmDelete}
        danger
        title={`Remove ${channelLabel[channel.channel_type]}?`}
        description={`Commerce Ops forgets this channel’s settings, mappings and publication history. Products already published to ${channelLabel[channel.channel_type]} stay there — take them down in the channel itself, or turn the channel off instead and let Commerce Ops withdraw them.`}
        confirmLabel="Remove channel"
        busy={busy === 'delete'}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          void act('delete', async () => {
            await deleteDistributionChannel(channel.id);
            setConfirmDelete(false);
            onClose();
          });
        }}
      />
    </>
  );
}

function GoogleFields({
  settings,
  onChange,
  disabled,
}: {
  settings: ChannelSettings;
  onChange: (next: ChannelSettings) => void;
  disabled: boolean;
}) {
  return (
    <>
      <Field label="Merchant Center account id">
        <Input
          id="google-account"
          value={settings.merchant_account_id ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, merchant_account_id: event.target.value })}
        />
      </Field>
      <Field
        label="Data source id"
        hint="Products can only be written into a data source of type API. Create one in Merchant Center first."
      >
        <Input
          id="google-datasource"
          value={settings.data_source_id ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, data_source_id: event.target.value })}
        />
      </Field>
      <Field label="Content language">
        <Input
          id="google-language"
          value={settings.content_language ?? ''}
          placeholder="ro"
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, content_language: event.target.value })}
        />
      </Field>
      <Field
        label="Feed label"
        hint="Google identifies a product by content language, feed label and SKU together."
      >
        <Input
          id="google-feedlabel"
          value={settings.feed_label ?? ''}
          placeholder="RO"
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, feed_label: event.target.value })}
        />
      </Field>
    </>
  );
}

function OpenAIFields({
  settings,
  onChange,
  disabled,
}: {
  settings: ChannelSettings;
  onChange: (next: ChannelSettings) => void;
  disabled: boolean;
}) {
  return (
    <>
      <Field
        label="Seller name"
        hint="Required in the feed and not held in the catalogue, so it is entered once here."
      >
        <Input
          id="acp-seller"
          value={settings.seller_name ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, seller_name: event.target.value })}
        />
      </Field>
      <Field label="SFTP host">
        <Input
          id="acp-host"
          value={settings.sftp_host ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, sftp_host: event.target.value })}
        />
      </Field>
      <Field label="SFTP user">
        <Input
          id="acp-user"
          value={settings.sftp_username ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, sftp_username: event.target.value })}
        />
      </Field>
      <Field label="Upload directory">
        <Input
          id="acp-dir"
          value={settings.sftp_directory ?? ''}
          placeholder="/upload"
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, sftp_directory: event.target.value })}
        />
      </Field>
      <Field
        label="Snapshot file name"
        hint="Keep the same name on every upload. The snapshot replaces the previous one."
      >
        <Input
          id="acp-file"
          value={settings.sftp_file_name ?? ''}
          placeholder="products.jsonl.gz"
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, sftp_file_name: event.target.value })}
        />
      </Field>
      <Checkbox
        checked={settings.enable_checkout ?? false}
        disabled={disabled}
        onChange={(event) => onChange({ ...settings, enable_checkout: event.target.checked })}
        label="Offer products for instant checkout"
      />
      {settings.enable_checkout ? (
        <>
          <Field label="Privacy policy URL">
            <Input
              id="acp-privacy"
              value={settings.seller_privacy_policy_url ?? ''}
              disabled={disabled}
              onChange={(event) => onChange({ ...settings, seller_privacy_policy_url: event.target.value })}
            />
          </Field>
          <Field label="Terms of service URL">
            <Input
              id="acp-terms"
              value={settings.seller_tos ?? ''}
              disabled={disabled}
              onChange={(event) => onChange({ ...settings, seller_tos: event.target.value })}
            />
          </Field>
        </>
      ) : null}
    </>
  );
}

/**
 * credentialLabel names the credential each channel actually wants. A Meta
 * system user token is one line, not a key file, and asking for it in a
 * four-row textarea labelled "private key" is how people paste the wrong
 * thing.
 */
const credentialLabel: Record<string, string> = {
  google_merchant: 'Google service account key',
  openai_acp: 'SFTP private key',
  meta_commerce: 'Meta system user access token',
  compari: '',
};

/**
 * MetaFields is everything a Meta connection needs, in the order a person
 * sets it up: get the feed URL, register it in Commerce Manager, then paste
 * back the ids that let Commerce Ops read the result.
 */
function MetaFields({
  channel,
  settings,
  onChange,
  disabled,
  busy,
  onRotate,
}: {
  channel: DistributionChannel;
  settings: ChannelSettings;
  onChange: (next: ChannelSettings) => void;
  disabled: boolean;
  busy: string | null;
  onRotate: () => void;
}) {
  return (
    <>
      <Field
        label="Feed URL"
        hint="In Commerce Manager, add a scheduled data source pointing at this URL and choose how often Meta should fetch it. Each fetch replaces the whole catalogue, so a product you stop publishing here is removed there. The URL carries a secret token — treat it like a password."
        action={
          !disabled ? (
            <button type="button" className="button button--secondary" disabled={busy !== null} onClick={onRotate}>
              {busy === 'feed-token' ? 'Issuing' : 'Issue a new URL'}
            </button>
          ) : null
        }
      >
        <Input id="meta-feed-url" readOnly value={channel.feed_url ?? 'Not generated yet'} />
      </Field>
      <Field label="Catalog ID" hint="From Commerce Manager. Used to read back each product’s review status.">
        <Input
          id="meta-catalog-id"
          inputMode="numeric"
          value={settings.meta_catalog_id ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, meta_catalog_id: event.target.value })}
        />
      </Field>
      <Field
        label="Data source ID"
        hint="The data source you pointed at the feed URL. Without it the feed still serves, but Commerce Ops cannot tell you whether Meta accepted it."
      >
        <Input
          id="meta-feed-id"
          inputMode="numeric"
          value={settings.meta_feed_id ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, meta_feed_id: event.target.value })}
        />
      </Field>
      <Field
        label="Business ID"
        hint="Optional. Recorded so this connection can be traced to the right Business Manager."
      >
        <Input
          id="meta-business-id"
          inputMode="numeric"
          value={settings.meta_business_id ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, meta_business_id: event.target.value })}
        />
      </Field>
      <Checkbox
        checked={settings.meta_use_facebook_category ?? false}
        disabled={disabled}
        onChange={(event) => onChange({ ...settings, meta_use_facebook_category: event.target.checked })}
        label="My category mappings use Meta’s own taxonomy"
        hint="Off, the feed sends the mapped category as a Google product category, which Meta also accepts. On, it sends it as a Facebook product category. Either way an unmapped category is left out rather than guessed."
      />
      <Checkbox
        checked={settings.meta_brand_fallback_to_seller ?? false}
        disabled={disabled}
        onChange={(event) => onChange({ ...settings, meta_brand_fallback_to_seller: event.target.checked })}
        label="Use my own name as the brand when a product has none"
        hint="Meta requires a brand on every product. Off, a product with no manufacturer is held back and listed as an issue. On, your name below is sent — which is a claim you are making, so it is recorded in the product’s provenance."
      />
      {settings.meta_brand_fallback_to_seller ? (
        <Field label="Your brand name">
          <Input
            id="meta-seller-name"
            value={settings.meta_seller_name ?? ''}
            disabled={disabled}
            onChange={(event) => onChange({ ...settings, meta_seller_name: event.target.value })}
          />
        </Field>
      ) : null}
    </>
  );
}

function CompariFields({
  channel,
  settings,
  onChange,
  disabled,
  busy,
  onRotate,
}: {
  channel: DistributionChannel;
  settings: ChannelSettings;
  onChange: (next: ChannelSettings) => void;
  disabled: boolean;
  busy: string | null;
  onRotate: () => void;
}) {
  return (
    <>
      <Field
        label="Feed URL"
        hint="Give this URL to Compari. It carries a secret token, so treat it like a password — anyone who has it can read this shop’s catalogue."
        action={
          !disabled ? (
            <button type="button" className="button button--secondary" disabled={busy !== null} onClick={onRotate}>
              {busy === 'feed-token' ? 'Issuing' : 'Issue a new URL'}
            </button>
          ) : null
        }
      >
        <Input id="compari-feed-url" readOnly value={channel.feed_url ?? 'Not generated yet'} />
      </Field>
      <Field
        label="Delivery time in working days"
        hint="Used for products whose delivery time is not in the catalogue. Commerce Ops does not guess one."
      >
        <Input
          id="compari-delivery-days"
          type="number"
          min={0}
          value={settings.default_delivery_days_max ?? 0}
          disabled={disabled}
          onChange={(event) => onChange({ ...settings, default_delivery_days_max: Number(event.target.value) })}
        />
      </Field>
      <Field label="Delivery cost" hint="Zero is published as free delivery.">
        <Input
          id="compari-delivery-cost"
          type="number"
          min={0}
          step="1"
          value={(settings.default_delivery_cost_micros ?? 0) / 1_000_000}
          disabled={disabled}
          onChange={(event) =>
            onChange({
              ...settings,
              default_delivery_cost_set: true,
              default_delivery_cost_micros: Math.round(Number(event.target.value) * 1_000_000),
            })
          }
        />
      </Field>
      <Checkbox
        checked={settings.include_attributes ?? false}
        disabled={disabled}
        onChange={(event) => onChange({ ...settings, include_attributes: event.target.checked })}
        label="Include mapped specifications in the feed"
      />
    </>
  );
}

function PolicyFields({
  policy,
  disabled,
  busy,
  onChange,
  onSave,
}: {
  policy: ChannelPolicy;
  disabled: boolean;
  busy: boolean;
  onChange: (next: ChannelPolicy) => void;
  onSave: () => void;
}) {
  const toggle = (key: keyof ChannelPolicy, label: string, help?: string) => (
    <Checkbox
      checked={Boolean(policy[key])}
      disabled={disabled}
      onChange={(event) => onChange({ ...policy, [key]: event.target.checked })}
      label={label}
      hint={help}
    />
  );

  return (
    <>
      {toggle('exclude_disabled', 'Leave out products that are disabled in the shop')}
      {toggle('exclude_without_image', 'Leave out products with no image')}
      {toggle(
        'exclude_not_visible_individually',
        'Leave out products hidden from the shop’s catalogue',
        'Variants are still published when the channel asks for variants — they are legitimately invisible on their own.',
      )}
      {toggle(
        'allow_without_price',
        'Publish products with no price',
        'For a website that takes quote requests instead of orders, where the channel accepts it.',
      )}
      {toggle('include_out_of_stock', 'Publish products that are out of stock')}
      {toggle('include_backorders', 'Publish products available on backorder')}

      <Field label="Which products of a variant family to publish">
        <Select
          id="policy-variants"
          value={policy.variant_publication}
          disabled={disabled}
          onChange={(event) => onChange({ ...policy, variant_publication: event.target.value as VariantPublication })}
        >
          {(['variants', 'parents', 'both'] as VariantPublication[]).map((value) => (
            <option key={value} value={value}>
              {variantPublicationLabel[value]}
            </option>
          ))}
        </Select>
      </Field>

      <Field
        label="Minimum quantity to publish"
        hint="Only applies where the shop reports a quantity. Zero means no minimum."
      >
        <Input
          id="policy-min-stock"
          type="number"
          min={0}
          value={policy.minimum_stock}
          disabled={disabled}
          onChange={(event) => onChange({ ...policy, minimum_stock: Number(event.target.value) })}
        />
      </Field>

      <Field label="Target country">
        <Input
          id="policy-country"
          value={policy.target_country ?? ''}
          placeholder="RO"
          disabled={disabled}
          onChange={(event) => onChange({ ...policy, target_country: event.target.value.toUpperCase() })}
        />
      </Field>

      {!disabled ? (
        <button type="button" className="button button--primary" disabled={busy} onClick={onSave} data-testid="channel-save-policy">
          {busy ? 'Saving' : 'Save and re-check every product'}
        </button>
      ) : null}
    </>
  );
}

function CategoryMappings({
  connectionId,
  channelType,
  canManage,
}: {
  connectionId: string;
  channelType: DistributionChannel['channel_type'];
  canManage: boolean;
}) {
  const mappings = useResource(() => listDistributionCategoryMappings(connectionId), [connectionId], connectionId);
  const [sourcePath, setSourcePath] = useState('');
  const [externalCategory, setExternalCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await setDistributionCategoryMapping(connectionId, {
        source_path: sourcePath,
        external_category: externalCategory,
      });
      setSourcePath('');
      setExternalCategory('');
      mappings.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  if (mappings.loading && !mappings.data) return <LoadingRows label="Loading category mappings" rows={4} />;
  if (mappings.error) return <ErrorNotice error={mappings.error} />;

  const unmapped = mappings.data?.unmapped ?? [];

  return (
    <>
      <p className="drawer-intro">A mapping on a parent category covers everything under it.</p>

      {error ? <p className="panel__note panel__note--danger">{error}</p> : null}

      {mappings.data?.mappings.length ? (
        <Table label="Category mappings" compact className="distribution-mappings-table">
          <thead>
            <tr>
              <th scope="col">Shop category</th>
              <th scope="col">{channelLabel[channelType]} category</th>
              {canManage ? <th scope="col"><span className="visually-hidden">Actions</span></th> : null}
            </tr>
          </thead>
          <tbody>
            {mappings.data.mappings.map((mapping) => (
              <tr key={mapping.id}>
                <th scope="row">{mapping.source_path}</th>
                <td>{mapping.external_category}</td>
                {canManage ? (
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={async () => {
                        await deleteDistributionCategoryMapping(mapping.id);
                        mappings.reload();
                      }}
                    >
                      Remove
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="quiet-empty">No mappings. The shop’s own category path is published as it is.</p>
      )}

      {unmapped.length > 0 ? (
        <details className="distribution-findings">
          <summary>Shop categories with no mapping ({unmapped.length})</summary>
          <ul>
            {unmapped.slice(0, 50).map((path) => (
              <li key={path}>
                <button type="button" className="link-button" onClick={() => setSourcePath(path)}>
                  {path}
                </button>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {canManage ? (
        <div className="distribution-mapping-form">
          <Field label="Shop category">
            <Input
              id="mapping-source"
              value={sourcePath}
              placeholder="Cazane &gt; Pe lemne"
              onChange={(event) => setSourcePath(event.target.value)}
            />
          </Field>
          <Field label={<>{channelLabel[channelType]} category</>}>
            <Input
              id="mapping-external"
              value={externalCategory}
              onChange={(event) => setExternalCategory(event.target.value)}
            />
          </Field>
          <button
            type="button"
            className="button button--secondary"
            disabled={busy || sourcePath.trim() === '' || externalCategory.trim() === ''}
            onClick={save}
          >
            {busy ? 'Saving' : 'Add mapping'}
          </button>
        </div>
      ) : null}
    </>
  );
}

/** The control each setup item is about, so the checklist can take the person to it. */
const setupFieldId: Record<string, string> = {
  credential: 'channel-credential',
  merchant_account_id: 'google-account',
  data_source_id: 'google-datasource',
  content_language: 'google-language',
  feed_label: 'google-feedlabel',
  seller_name: 'acp-seller',
  sftp_host: 'acp-host',
  sftp_username: 'acp-user',
  sftp_directory: 'acp-dir',
  sftp_file_name: 'acp-file',
  meta_catalog_id: 'meta-catalog-id',
  meta_feed_id: 'meta-feed-id',
  meta_business_id: 'meta-business-id',
  feed_url: 'meta-feed-url',
};

const setupFieldLabel: Record<string, string> = {
  credential: 'Credential',
  merchant_account_id: 'Merchant Center account id',
  data_source_id: 'Data source id',
  content_language: 'Content language',
  feed_label: 'Feed label',
  seller_name: 'Seller name',
  sftp_host: 'SFTP host',
  sftp_username: 'SFTP user',
  sftp_directory: 'SFTP directory',
  sftp_file_name: 'SFTP file name',
  meta_catalog_id: 'Catalog id',
  meta_feed_id: 'Data source id',
  meta_business_id: 'Business id',
  feed_url: 'Feed URL',
};

/**
 * What a new merchant still has to do, in order: the channel's own missing
 * settings (required first), then a connection check, then publishing. It
 * disappears once the channel is set up, checked and publishing, so a working
 * channel shows nothing here.
 */
function SetupChecklist({ channel, busy, canManage, onCheck }: { channel: DistributionChannel; busy: boolean; canManage: boolean; onCheck: () => void }) {
  const missing = channel.setup ?? [];
  const requiredLeft = missing.some((item) => item.required);
  const connected = channel.health === 'connected';
  if (missing.length === 0 && connected && channel.enabled) return null;

  const items: ChecklistItem[] = missing.map((item) => {
    const target = setupFieldId[item.field];
    return {
      key: item.field,
      label: setupFieldLabel[item.field] ?? item.message,
      state: 'todo',
      note: item.required ? undefined : 'recommended',
      action: target ? (
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => document.getElementById(target)?.focus()}
          aria-label={`Go to ${setupFieldLabel[item.field] ?? item.field}`}
        >
          Go to
        </button>
      ) : undefined,
    };
  });
  items.push({
    key: 'check',
    label: 'Connection checked',
    state: connected ? 'done' : 'todo',
    note: !connected && channel.health_detail && !requiredLeft ? channel.health_detail : undefined,
    action: !connected ? (
      <button type="button" className="button button--secondary button--small" disabled={!canManage || busy || requiredLeft} onClick={onCheck} data-testid="channel-check-connection">
        Check connection
      </button>
    ) : undefined,
  });
  items.push({ key: 'publish', label: `Publishing to ${channelLabel[channel.channel_type]}`, state: channel.enabled ? 'done' : 'todo' });
  return <Checklist label={`Set up ${channelLabel[channel.channel_type]}`} items={items} testId="channel-setup" />;
}
